import { getBackgroundColorClass, getColorClass } from "@/lib/colors";
import type React from "react";
import type { TriggerEventContext, TriggerRenderer, TriggerRendererContext } from "../types";
import { renderTimeAgo, renderWithTimeAgo } from "@/components/TimeAgo";
import type { TriggerProps } from "@/ui/trigger";
import slackIcon from "@/assets/icons/integrations/slack.svg";

interface OnAppMentionConfiguration {
  channel?: string;
}

interface OnAppMentionMetadata {
  channel?: {
    id?: string;
    name?: string;
  };
}

interface AppMentionEventData {
  channel?: string;
  text?: string;
  user?: string;
  ts?: string;
  event_ts?: string;
  thread_ts?: string;
}

/**
 * Renderer for the "slack.onAppMention" trigger
 */
export const onAppMentionTriggerRenderer: TriggerRenderer = {
  getTitleAndSubtitle: (context: TriggerEventContext): { title: string; subtitle: string | React.ReactNode } => {
    const eventData = context.event?.data as AppMentionEventData | undefined;
    const title = eventData?.text?.trim() ? eventData.text : "App mention";
    const subtitle = buildSubtitle(
      eventData?.user ? `Mention by ${eventData.user}` : "Mention",
      context.event?.createdAt,
    );

    return {
      title,
      subtitle,
    };
  },

  getRootEventValues: (context: TriggerEventContext): Record<string, string> => {
    const eventData = context.event?.data as AppMentionEventData | undefined;
    const mentionedAt = formatSlackTimestamp(eventData?.ts || eventData?.event_ts);

    return {
      "Mentioned At": mentionedAt || "",
      Channel: stringOrDash(eventData?.channel),
      User: stringOrDash(eventData?.user),
      Text: stringOrDash(eventData?.text),
      "Thread Timestamp": stringOrDash(eventData?.thread_ts),
    };
  },

  getTriggerProps: (context: TriggerRendererContext) => {
    const { node, definition, lastEvent } = context;
    const metadata = node.metadata as OnAppMentionMetadata | undefined;
    const configuration = node.configuration as OnAppMentionConfiguration | undefined;
    const metadataItems = [];

    const channelLabel = metadata?.channel?.name || configuration?.channel;
    if (channelLabel) {
      metadataItems.push({
        icon: "hash",
        label: channelLabel,
      });
    }

    const props: TriggerProps = {
      title: node.name || definition.label || "Unnamed trigger",
      iconSrc: slackIcon,
      iconSlug: "slack",
      iconColor: getColorClass(definition.color),
      collapsedBackground: getBackgroundColorClass(definition.color),
      metadata: metadataItems,
    };

    if (lastEvent) {
      const eventData = lastEvent.data as AppMentionEventData | undefined;
      const title = eventData?.text?.trim() ? eventData.text : "App mention";
      const subtitle = buildSubtitle(eventData?.user ? `Mention by ${eventData.user}` : "Mention", lastEvent.createdAt);

      props.lastEventData = {
        title,
        subtitle,
        receivedAt: new Date(lastEvent.createdAt),
        state: "triggered",
        eventId: lastEvent.id,
      };
    }

    return props;
  },
};

function stringOrDash(value?: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return String(value);
}

function buildSubtitle(content: string, createdAt?: string): string | React.ReactNode {
  if (content && createdAt) {
    return renderWithTimeAgo(content, new Date(createdAt));
  }
  return content || (createdAt ? renderTimeAgo(new Date(createdAt)) : "");
}

function formatSlackTimestamp(value?: unknown): string | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const raw = String(value);
  const seconds = Number.parseFloat(raw);
  if (!Number.isNaN(seconds)) {
    return new Date(seconds * 1000).toLocaleString();
  }

  const asDate = new Date(raw);
  if (!Number.isNaN(asDate.getTime())) {
    return asDate.toLocaleString();
  }

  return raw;
}
